import { useMemo } from 'react';

import { useSession } from '@ais-ui/websockets';
import { useCredentials } from '@ais-ui/auth';
import { useBasePath, GC_TIMES } from '@ais-ui/custom-hooks';

import { getBasePath } from './getAISBasePath';
import { Configuration, DatabaseLoaderApi } from '@ais-ui/api-spec/dbloader';
import { useTrackedQuery as useQuery } from '@ais-ui/performance-tracking';
import { getAuthHeaderFromCredentials } from '@ais-ui/utils';
import { importGeoJsonTracks } from '@ais-ui/synthetic-geo';
import type { ImportResult } from '@ais-ui/synthetic-geo';

export type { ImportResult };

export const useDbloaderApi = () => {
  const credentials = useCredentials();
  const { sessionId } = useSession();
  const { protocol, hostname } = useBasePath();

  let basePath = getBasePath();
  let configuration: Configuration;
  if (hostname.includes('localhost')) {
    const username = credentials?.username || '';
    const password = credentials?.password || '';

    const authHeader = getAuthHeaderFromCredentials(username, password);
    configuration = new Configuration({
      basePath: `${basePath}api/dbloader`,
      headers: {
        'X-Application-Name': 'marinepulse',
        Authorization: authHeader,
      },
    });
  } else {
    basePath = `${basePath}aisweb/api/dbloader`;
    configuration = new Configuration({
      basePath: basePath,
      headers: {
        'Aisweb-Session': sessionId,
        'X-Application-Name': 'marinepulse',
      },
    });
  }

  const dbloaderApi = useMemo(
    () => new DatabaseLoaderApi(configuration),
    [configuration],
  );
  return dbloaderApi;
};

export const useDatabaseRange = (_options = {}) => {
  const dbloaderApi = useDbloaderApi();

  return useQuery(
    {
      queryKey: ['databaseRange'],
      queryFn: async () => {
        const response = await dbloaderApi.dailyRangeGet();
        return response;
      },
      gcTime: GC_TIMES.LONG,
      retry: false,
    },
    'dbloader:databaseRange',
  );
};

// --- Vector Track (GeoJSON) ---

export interface VectorTrackParams {
  mmsi: number;
  startTime: Date;
  endTime: Date;
  resolutionSec?: number;
}

/**
 * Imperative function to fetch a vessel track as GeoJSON and parse it into
 * LineString tracks using the synthetic-geo importer.
 *
 * Use this from callbacks/event handlers. For declarative (render-driven)
 * fetching, use the `useVectorTrack` hook instead.
 */
export async function fetchVectorTrack(
  params: VectorTrackParams,
  options: { basePath: string; headers: Record<string, string> },
): Promise<ImportResult> {
  const formatDateTime = (d: Date) => d.toISOString().replace(/\.\d{3}Z$/, '');

  const formatResolution = (totalSec: number) => {
    const h = String(Math.floor(totalSec / 3600)).padStart(2, '0');
    const m = String(Math.floor((totalSec % 3600) / 60)).padStart(2, '0');
    const s = String(totalSec % 60).padStart(2, '0');
    return `${h}:${m}:${s}`;
  };

  const searchParams = new URLSearchParams({
    mmsi: params.mmsi.toString(),
    begin: formatDateTime(params.startTime),
    end: formatDateTime(params.endTime),
  });

  if (params.resolutionSec != null) {
    searchParams.set('resolution', formatResolution(params.resolutionSec));
  }

  const url = `${options.basePath}/daily/track/geojson?${searchParams.toString()}`;
  const response = await fetch(url, { headers: options.headers });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(
      (errorBody as Record<string, string>).error ??
        `Failed to fetch vector track: ${response.status}`,
    );
  }

  const data = await response.json();

  // An empty track is a valid outcome — the vessel simply reported no positions
  // in the requested window. Surface it as an empty result instead of letting
  // the importer throw, so callers can show an informative message rather than
  // treating it as a failure.
  const features = (data?.track as { features?: unknown[] } | undefined)
    ?.features;
  if (!Array.isArray(features) || features.length === 0) {
    return {
      name: '',
      start_time: '',
      end_time: '',
      tracks: [],
      bounds: [0, 0, 0, 0],
    };
  }

  return importGeoJsonTracks(data.track);
}

/** Hook providing a pre-configured `fetchVectorTrack` bound to the current auth context. */
export const useFetchVectorTrack = () => {
  const credentials = useCredentials();
  const { sessionId } = useSession();
  const { hostname } = useBasePath();

  const isLocal = hostname.includes('localhost');
  let basePath = getBasePath();
  if (!isLocal) {
    basePath = `${basePath}aisweb/api/dbloader`;
  } else {
    basePath = `${basePath}api/dbloader`;
  }

  const headers = useMemo((): Record<string, string> => {
    if (isLocal) {
      const username = credentials?.username || '';
      const password = credentials?.password || '';
      return {
        'X-Application-Name': 'marinepulse',
        Authorization: getAuthHeaderFromCredentials(username, password),
      };
    }
    return {
      'Aisweb-Session': sessionId,
      'X-Application-Name': 'marinepulse',
    };
  }, [isLocal, credentials, sessionId]);

  return useMemo(
    () => (params: VectorTrackParams) =>
      fetchVectorTrack(params, { basePath, headers }),
    [basePath, headers],
  );
};

/**
 * Declarative hook to fetch a vessel track as GeoJSON.
 * Automatically refetches when params change.
 */
export const useVectorTrack = (
  params: VectorTrackParams | null,
  enabled = true,
) => {
  const fetchTrack = useFetchVectorTrack();

  return useQuery(
    {
      queryKey: [
        'vectorTrack',
        params?.mmsi,
        params?.startTime?.toISOString(),
        params?.endTime?.toISOString(),
        params?.resolutionSec,
      ],
      queryFn: () => fetchTrack(params!),
      enabled: enabled && params != null,
      retry: false,
    },
    'dbloader:vectorTrack',
  );
};
